import * as React from "react"
import { appPath } from "@/shared/lib/appPath"
import type { VisitCardDTO } from "@/api/visit_card_client"
import type { CopilotRecommendation } from "../hooks/useCopilot"
import { VISIT_CARD_FLASH_MS } from "../hooks/useVisitCard"
import {
  applyDemoVisitCardUpdate,
  cloneDemoVisitCard,
} from "./demoVisitCard"
import {
  DEMO_CHECKPOINTS,
  DEMO_COPILOT_RECS,
  DEMO_DIALOG_AUDIO_PATH,
  DEMO_START_RECORDING_STEP_ID,
  DEMO_VISIT_CARD_UPDATES,
  dueDemoCheckpoints,
  dueDemoRecommendations,
  dueDemoVisitCardUpdates,
} from "./demoPlayback"

export type DemoPlaybackStopOptions = {
  /** When true (default while playing), persist the demo recording after stop. */
  persist?: boolean
}

export type UseDemoMeetingPlaybackArgs = {
  /** Current tour step id, or null when tour is not running. */
  tourStepId: string | null
  /** True while the interactive tour is active. */
  isTourRunning: boolean
  appendRecommendations: (recs: CopilotRecommendation[]) => void
  applyCheckpoints: (keys: string[]) => void
  resetCheckpointsForDemo: () => void
  markMissedCheckpoints: () => void
  /** Called once when playback ends naturally or the user stops the recorder. */
  onPersistRecord?: (info: { durationSec: number }) => void
}

export type UseDemoMeetingPlaybackResult = {
  /** Script is armed (tour on start-recording) or already playing. */
  isArmed: boolean
  /** Demo audio is currently playing. */
  isPlaying: boolean
  /** Keep Copilot panel visible after script ends (until clear / live start). */
  keepPanel: boolean
  /** Elapsed playback time in seconds (for recorder UI timer). */
  elapsedSec: number
  /** Live demo visit card (base + timed LLM fills). Null until first start. */
  visitCard: VisitCardDTO | null
  /** FlatKeys of fields that just filled (same flash UX as live). */
  visitCardFlashes: string[]
  start: () => void
  stop: (opts?: DemoPlaybackStopOptions) => void
  /** Clear keepPanel after user clears recommendations or starts live recording. */
  dismissPanel: () => void
}

function audioDurationSec(audio: HTMLAudioElement | null): number {
  const duration = audio?.duration
  if (duration != null && Number.isFinite(duration) && duration > 0) {
    return duration
  }
  return 0
}

/**
 * Scripted demo meeting: play dialog_demo.mp3 and inject recommendations
 * / checkpoints / visit-card field fills from JSON by audio currentTime vs t_sec.
 * No mic, no copilot backend.
 */
export function useDemoMeetingPlayback({
  tourStepId,
  isTourRunning,
  appendRecommendations,
  applyCheckpoints,
  resetCheckpointsForDemo,
  markMissedCheckpoints,
  onPersistRecord,
}: UseDemoMeetingPlaybackArgs): UseDemoMeetingPlaybackResult {
  const [isPlaying, setIsPlaying] = React.useState(false)
  const [keepPanel, setKeepPanel] = React.useState(false)
  const [elapsedSec, setElapsedSec] = React.useState(0)
  const [visitCard, setVisitCard] = React.useState<VisitCardDTO | null>(null)
  const [visitCardFlashes, setVisitCardFlashes] = React.useState<string[]>([])

  const audioRef = React.useRef<HTMLAudioElement | null>(null)
  const emittedRecsRef = React.useRef<Set<number>>(new Set())
  const emittedCheckpointsRef = React.useRef<Set<number>>(new Set())
  const emittedVisitCardRef = React.useRef<Set<number>>(new Set())
  const visitCardRef = React.useRef<VisitCardDTO | null>(null)
  const flashTimerRef = React.useRef<number | null>(null)
  const didPersistRef = React.useRef(false)
  const listenersRef = React.useRef<{
    timeupdate: () => void
    ended: () => void
    error: () => void
  } | null>(null)
  const appendRef = React.useRef(appendRecommendations)
  React.useEffect(() => {
    appendRef.current = appendRecommendations
  }, [appendRecommendations])
  const applyCheckpointsRef = React.useRef(applyCheckpoints)
  React.useEffect(() => {
    applyCheckpointsRef.current = applyCheckpoints
  }, [applyCheckpoints])
  const resetCheckpointsRef = React.useRef(resetCheckpointsForDemo)
  React.useEffect(() => {
    resetCheckpointsRef.current = resetCheckpointsForDemo
  }, [resetCheckpointsForDemo])
  const markMissedRef = React.useRef(markMissedCheckpoints)
  React.useEffect(() => {
    markMissedRef.current = markMissedCheckpoints
  }, [markMissedCheckpoints])
  const persistRef = React.useRef(onPersistRecord)
  React.useEffect(() => {
    persistRef.current = onPersistRecord
  }, [onPersistRecord])

  const clearFlashTimer = React.useCallback(() => {
    if (flashTimerRef.current !== null) {
      window.clearTimeout(flashTimerRef.current)
      flashTimerRef.current = null
    }
  }, [])

  const flashFields = React.useCallback(
    (flatKeys: string[]) => {
      if (flatKeys.length === 0) return
      setVisitCardFlashes((prev) => Array.from(new Set([...prev, ...flatKeys])))
      clearFlashTimer()
      flashTimerRef.current = window.setTimeout(() => {
        flashTimerRef.current = null
        setVisitCardFlashes([])
      }, VISIT_CARD_FLASH_MS)
    },
    [clearFlashTimer],
  )

  const isArmed =
    isPlaying ||
    (isTourRunning && tourStepId === DEMO_START_RECORDING_STEP_ID)

  const detachListeners = React.useCallback((audio: HTMLAudioElement) => {
    const listeners = listenersRef.current
    if (!listeners) return
    audio.removeEventListener("timeupdate", listeners.timeupdate)
    audio.removeEventListener("ended", listeners.ended)
    audio.removeEventListener("error", listeners.error)
    listenersRef.current = null
  }, [])

  const cleanupAudio = React.useCallback(() => {
    const audio = audioRef.current
    if (!audio) return
    detachListeners(audio)
    audio.pause()
    audio.removeAttribute("src")
    audio.load()
    audioRef.current = null
  }, [detachListeners])

  const finishPlayback = React.useCallback((opts?: { persist?: boolean }) => {
    const durationSec = audioDurationSec(audioRef.current)
    cleanupAudio()
    setIsPlaying(false)
    setElapsedSec(0)
    setKeepPanel(true)
    markMissedRef.current()
    emittedRecsRef.current = new Set()
    emittedCheckpointsRef.current = new Set()
    // Keep emittedVisitCardRef / visitCard as-is — no catch-up on stop.
    if (opts?.persist && !didPersistRef.current) {
      didPersistRef.current = true
      persistRef.current?.({ durationSec })
    }
  }, [cleanupAudio])

  const stop = React.useCallback((opts?: DemoPlaybackStopOptions) => {
    if (!audioRef.current && !isPlaying) {
      cleanupAudio()
      setIsPlaying(false)
      return
    }
    finishPlayback({ persist: opts?.persist !== false })
  }, [cleanupAudio, finishPlayback, isPlaying])

  const flushDue = React.useCallback((currentTime: number) => {
    const dueRecs = dueDemoRecommendations(currentTime, emittedRecsRef.current)
    if (dueRecs.length > 0) {
      for (let i = 0; i < DEMO_COPILOT_RECS.length; i += 1) {
        if (emittedRecsRef.current.has(i)) continue
        if (DEMO_COPILOT_RECS[i].t_sec <= currentTime) {
          emittedRecsRef.current.add(i)
        }
      }
      // Newest first — same as live SSE prepend order.
      appendRef.current([...dueRecs].reverse())
    }

    const dueCps = dueDemoCheckpoints(currentTime, emittedCheckpointsRef.current)
    if (dueCps.length > 0) {
      for (let i = 0; i < DEMO_CHECKPOINTS.length; i += 1) {
        if (emittedCheckpointsRef.current.has(i)) continue
        if (DEMO_CHECKPOINTS[i].t_sec <= currentTime) {
          emittedCheckpointsRef.current.add(i)
        }
      }
      applyCheckpointsRef.current(dueCps)
    }

    const dueCard = dueDemoVisitCardUpdates(currentTime, emittedVisitCardRef.current)
    if (dueCard.length > 0) {
      for (let i = 0; i < DEMO_VISIT_CARD_UPDATES.length; i += 1) {
        if (emittedVisitCardRef.current.has(i)) continue
        if (DEMO_VISIT_CARD_UPDATES[i].t_sec <= currentTime) {
          emittedVisitCardRef.current.add(i)
        }
      }
      let next = visitCardRef.current ?? cloneDemoVisitCard()
      const flashed: string[] = []
      for (const update of dueCard) {
        const applied = applyDemoVisitCardUpdate(next, update.fields)
        next = applied.card
        flashed.push(...applied.flashed)
      }
      visitCardRef.current = next
      setVisitCard(next)
      flashFields(flashed)
    }
  }, [flashFields])

  const start = React.useCallback(() => {
    if (isPlaying) return
    cleanupAudio()
    emittedRecsRef.current = new Set()
    emittedCheckpointsRef.current = new Set()
    emittedVisitCardRef.current = new Set()
    didPersistRef.current = false
    clearFlashTimer()
    setVisitCardFlashes([])
    const base = cloneDemoVisitCard()
    visitCardRef.current = base
    setVisitCard(base)
    setElapsedSec(0)
    setKeepPanel(true)
    resetCheckpointsRef.current()

    const audio = new Audio(appPath(DEMO_DIALOG_AUDIO_PATH))
    audioRef.current = audio

    const onTimeUpdate = () => {
      const t = audio.currentTime
      setElapsedSec(t)
      flushDue(t)
    }
    const onEnded = () => {
      finishPlayback({ persist: true })
    }
    const onError = () => {
      console.error(
        "[demoMeetingPlayback] audio failed to load",
        audio.src,
        audio.error,
      )
      finishPlayback({ persist: false })
    }

    listenersRef.current = {
      timeupdate: onTimeUpdate,
      ended: onEnded,
      error: onError,
    }
    audio.addEventListener("timeupdate", onTimeUpdate)
    audio.addEventListener("ended", onEnded)
    audio.addEventListener("error", onError)

    setIsPlaying(true)
    void audio.play().catch((err) => {
      console.error("[demoMeetingPlayback] audio.play() rejected", audio.src, err)
      finishPlayback({ persist: false })
    })
  }, [cleanupAudio, clearFlashTimer, finishPlayback, flushDue, isPlaying])

  // Skip tour / leave tour mode: call stop({ persist: false }) from the page —
  // do not auto-stop when the last Next ends the tour (mp3 should keep playing).

  React.useEffect(() => {
    return () => {
      clearFlashTimer()
      cleanupAudio()
    }
  }, [cleanupAudio, clearFlashTimer])

  const dismissPanel = React.useCallback(() => {
    setKeepPanel(false)
  }, [])

  return {
    isArmed,
    isPlaying,
    keepPanel,
    elapsedSec,
    visitCard,
    visitCardFlashes,
    start,
    stop,
    dismissPanel,
  }
}
